import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Paper,
  Typography,
  Select,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
  LinearProgress,
  Chip,
  CircularProgress,
  Alert,
} from '@mui/material';
import { useCreditMode } from './CreditModeContext';
import {
  listPeriods,
  currentPeriod,
  monthLabel,
  formatCredits,
  formatCost,
} from '../../utils/creditFormat';

// Shared presentational primitives for the AI Usage screens. Purely visual — they own no
// data. Every credit figure is rendered through the CreditModeContext `format` so the one
// Credit/Cost toggle flips every number on screen at once.

// The metering start month is not yet exposed by the API, so the picker offers the last
// ~12 months. TODO: bound the earliest option to the real metering-start period once the
// API surfaces it, so operators cannot pick months that predate any captured data.
const METERING_MONTHS = 12;

export function monthOptions(now = new Date()) {
  const to = currentPeriod(now);
  const [y, m] = to.split('-').map(Number);
  let fy = y;
  let fm = m - (METERING_MONTHS - 1);
  while (fm <= 0) {
    fm += 12;
    fy -= 1;
  }
  return listPeriods(`${fy}-${String(fm).padStart(2, '0')}`, to);
}

export function MonthDropdown({ value, onChange }) {
  return (
    <Select
      size='small'
      value={value}
      onChange={(e) => onChange(e.target.value)}
      inputProps={{ 'aria-label': 'Month' }}
      data-testid='month-dropdown'
      sx={{ minWidth: 160 }}
    >
      {monthOptions().map((period) => (
        <MenuItem key={period} value={period}>
          {monthLabel(period)}
        </MenuItem>
      ))}
    </Select>
  );
}

MonthDropdown.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
};

export function CreditModeToggle() {
  const { mode, setMode } = useCreditMode();
  return (
    <ToggleButtonGroup
      exclusive
      size='small'
      value={mode}
      onChange={(_e, next) => next && setMode(next)}
      data-testid='credit-mode-toggle'
    >
      <ToggleButton value='credit'>Credits</ToggleButton>
      <ToggleButton value='cost'>Cost</ToggleButton>
    </ToggleButtonGroup>
  );
}

export function SortByDropdown({ value, options, onChange, label = 'Sort by' }) {
  return (
    <Select
      size='small'
      value={value}
      onChange={(e) => onChange(e.target.value)}
      inputProps={{ 'aria-label': label }}
      data-testid='sort-by-dropdown'
      sx={{ minWidth: 180 }}
    >
      {options.map((opt) => (
        <MenuItem key={opt.key} value={opt.key}>
          {opt.label}
        </MenuItem>
      ))}
    </Select>
  );
}

SortByDropdown.propTypes = {
  value: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({ key: PropTypes.string, label: PropTypes.string })
  ).isRequired,
  onChange: PropTypes.func.isRequired,
  label: PropTypes.string,
};

// A single headline figure.
//  - 'toggle' (default): a credit value rendered in whichever unit the Credit/Cost toggle
//     currently shows.
//  - 'credit' / 'cost': a credit value pinned to that unit regardless of the toggle (for the
//     paired "Total Credits Used" / "Total Cost" tiles).
//  - 'raw': a value that is not credits (counts, org totals) — shown as-is.
export function SummaryTile({ label, value, mode = 'toggle' }) {
  const credit = useCreditMode();
  const display =
    mode === 'raw'
      ? value
      : mode === 'credit'
      ? formatCredits(value)
      : mode === 'cost'
      ? formatCost(value)
      : credit.format(value);
  return (
    <Paper variant='outlined' sx={{ p: 2, borderRadius: 2, flex: 1, minWidth: 150 }}>
      <Typography variant='caption' color='text.secondary'>
        {label}
      </Typography>
      <Typography variant='h5' sx={{ fontWeight: 700, mt: 0.5 }}>
        {display}
      </Typography>
    </Paper>
  );
}

SummaryTile.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  mode: PropTypes.oneOf(['toggle', 'credit', 'cost', 'raw']),
};

export function UsageBar({ pct, height = 8 }) {
  const value = Math.max(0, Math.min(100, Number(pct) || 0));
  const color = value >= 100 ? 'error' : value >= 80 ? 'warning' : 'success';
  return (
    <LinearProgress
      variant='determinate'
      value={value}
      color={color}
      sx={{ height, borderRadius: height / 2 }}
    />
  );
}

UsageBar.propTypes = {
  pct: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  height: PropTypes.number,
};

export function TypeBadge({ type }) {
  if (!type) return null;
  return (
    <Chip
      size='small'
      label={type}
      color={type === 'GROUP' ? 'primary' : 'default'}
      variant='outlined'
    />
  );
}

TypeBadge.propTypes = { type: PropTypes.oneOf(['GROUP', 'SCHOOL']) };

// Uniform loading / error / empty states so every section behaves the same.
export function SectionStates({ loading, error, isEmpty, emptyText = 'No usage recorded for this period.', onRetry }) {
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (error) {
    return (
      <Alert
        severity='error'
        sx={{ my: 2 }}
        action={
          onRetry ? (
            <Typography
              component='button'
              onClick={onRetry}
              sx={{ background: 'none', border: 0, cursor: 'pointer', color: 'inherit', textDecoration: 'underline' }}
            >
              Retry
            </Typography>
          ) : null
        }
      >
        {error}
      </Alert>
    );
  }
  if (isEmpty) {
    return (
      <Box sx={{ py: 5, textAlign: 'center' }}>
        <Typography variant='body2' color='text.secondary'>
          {emptyText}
        </Typography>
      </Box>
    );
  }
  return null;
}

SectionStates.propTypes = {
  loading: PropTypes.bool,
  error: PropTypes.string,
  isEmpty: PropTypes.bool,
  emptyText: PropTypes.string,
  onRetry: PropTypes.func,
};

export function SectionHeader({ title, children }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 1.5,
        mb: 2,
      }}
    >
      <Typography variant='h6' component='h3' sx={{ fontWeight: 700 }}>
        {title}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        {children}
      </Box>
    </Box>
  );
}

SectionHeader.propTypes = {
  title: PropTypes.string.isRequired,
  children: PropTypes.node,
};
